import { createRoot } from 'react-dom/client';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrollStack, { ScrollStackItem } from './components/ScrollStack.jsx';

gsap.registerPlugin(ScrollTrigger);

const rootEl = document.getElementById('insightsScrollStackRoot');

function resolveAssetUrl(path) {
	if (!path) {
		return '';
	}
	if (/^https?:\/\//i.test(path)) {
		return path;
	}
	const base = rootEl?.dataset?.themeUri?.replace(/\/$/, '') ?? '';
	const segments = path.replace(/^\//, '').split('/').map((s) => encodeURIComponent(s));
	return base ? `${base}/${segments.join('/')}` : segments.join('/');
}

function WorkCardContent({
	title,
	desc,
	logo,
	logoAlt = '',
	badge,
	badgeAlt = '',
	btnLabel = 'View Project',
	thumbUrls = [],
	url = '',
}) {
	const logoSrc = resolveAssetUrl(logo);
	const badgeSrc = resolveAssetUrl(badge);
	const thumbSlots = Array.from({ length: 4 }, (_, i) => thumbUrls[i] ?? '');

	const logoNode = logoSrc ? (
		<img
			className="work-card__logo"
			src={logoSrc}
			alt={logoAlt}
			decoding="async"
			loading="lazy"
		/>
	) : (
		<div className="work-card__logo-placeholder" aria-hidden="true" />
	);

	return (
		<div className="work-card__inner">
			<div className="work-card__top">
				<div className="work-card__copy">
					<h3 className="work-card__title">{title}</h3>
					<p className="work-card__desc">{desc}</p>
					<a className="work-card__btn" href={url || '#'}>
						{btnLabel}
					</a>
				</div>
				{badgeSrc ? (
					<div className={`work-card__logo-group ${badgeSrc ? 'doble-image' : ''}`} >
						<img
							className="work-card__badge"
							src={badgeSrc}
							alt={badgeAlt}
							decoding="async"
							loading="lazy"
						/>
						{logoNode}
					</div>
				) : (
					logoNode
				)}
			</div>
			<div className="work-card__thumbs" aria-label="Project previews">
				{thumbSlots.map((thumbPath, index) => {
					const src = resolveAssetUrl(thumbPath);
					return src ? (
						<span key={index} className="work-card__thumb">
							<img src={src} alt="" decoding="async" loading="lazy" />
						</span>
					) : (
						<span key={index} className="work-card__thumb" />
					);
				})}
			</div>
		</div>
	);
}

if (rootEl) {
	const root = createRoot(rootEl);

	root.render(
		<ScrollStack
			className="insights-scroll-stack__scroller"
			useWindowScroll
			itemDistance={80}
			itemScale={0.1}
			itemStackDistance={[60, 67, 75, 75]}
			stackPosition="100px"
			scaleEndPosition="10%"
			baseScale={0.7}
			blurAmount={0}
		>
			<ScrollStackItem itemClassName="work-card work-card--tone-4">
				<WorkCardContent
					title="Technical Learning Academy"
					desc="We helped create 40+ training courses for a pensions administration company, reducing training and certification time from one year to under three months while building real expertise."
					badge="images/client-logos/aptia-badge.png"
					badgeAlt="Brandon Hall Group Gold 2022 HCM Excellence Award"
					logo="images/client-logos/aptia-card.png"
					logoAlt="Aptia"
					url="/project/aptia/"
					thumbUrls={[
						'images/client-logos/aptia-thumb-1.png',
						'images/client-logos/aptia-thumb-2.png',
						'images/client-logos/aptia-thumb-3.png',
						'images/client-logos/aptia-thumb-4.png',
					]}
				/>
			</ScrollStackItem>
			<ScrollStackItem itemClassName="work-card work-card--tone-2">
				<WorkCardContent
					title="Scenario Driven Workplace Skills"
					desc="We created a series of characters and built multiple technical and workplace skills courses around them, making the training feel realistic, relatable, and connected to employees’ day-to-day work."
					logo="images/client-logos/merit-card.png"
					logoAlt="Merit"
					url="/project/merit/"
					thumbUrls={[
						'images/client-logos/merit-thumb-1.png',
						'images/client-logos/merit-thumb-2.png',
						'images/client-logos/merit-thumb-3.png',
						'images/client-logos/merit-thumb-4.png',
					]}
				/>
			</ScrollStackItem>
			<ScrollStackItem itemClassName="work-card work-card--tone-3">
				<WorkCardContent
					title="Soft Skills Library"
					desc="We created a 25+ course library across multiple topics, complete with facilitator guides, workbooks, teasers, activity sheets, and assessments, ready for the company’s trainers to plug and play."
					logo="images/client-logos/focusu-card.png"
					logoAlt="FocusU"
					url="/project/focusu/"
					thumbUrls={[
						'images/client-logos/focus-thumb-1.png',
						'images/client-logos/focus-thumb-2.png',
						'images/client-logos/focus-thumb-3.png',
						'images/client-logos/focus-thumb-4.png',
					]}
				/>
			</ScrollStackItem>
			<ScrollStackItem itemClassName="work-card work-card--tone-1">
				<WorkCardContent
					title="Regional Learning"
					desc="We created multilingual training programs for an Indian nonprofit, in English and regional languages, to help frontline teams engage stakeholders more effectively and create greater impact."
					logo="images/client-logos/miracle-foundation-card.png"
					logoAlt="Miracle Foundation"
					url="/project/miracle/"
					thumbUrls={[
						'images/client-logos/miracle-thumb-1.png',
						'images/client-logos/miracle-thumb-2.png',
						'images/client-logos/miracle-thumb-3.png',
						'images/client-logos/miracle-thumb-4.png',
					]}
				/>
			</ScrollStackItem>
		</ScrollStack>
	);
}
